import PropTypes from 'prop-types';
import { useRef } from 'react';

import Image from 'components/Image';

import useIntersectionObserver from 'hooks/useIntersectionObserver';

import { sendEvent } from 'lib/gtag';
import trackEvent from 'lib/posthog/trackEvent';

import APP from 'constants/app';

const AdCarouselItem = ({
  id,
  description,
  desktopImage,
  gaEventCategory,
  mobileImage,
  position,
  priority,
  tabletImage,
  visibility,
  url,
}) => {
  const itemRef = useRef(null);
  const viewFiredRef = useRef(false);

  useIntersectionObserver({
    target: itemRef,
    threshold: 0.5,
    onIntersect: () => {
      if (viewFiredRef.current) return;
      viewFiredRef.current = true;

      trackEvent(
        'view home banner',
        {
          type: 'banner',
          source_type: 'scroll',
          source_value: description,
          event_category: 'home',
          event_label: description,
          position,
        },
        { destination: 'ga4' }
      );
    },
  });

  const sharedChildren = (
    <>
      <Image
        alt={description}
        className="rounded-3 border border-neutral-high-200 bg-neutral-high-100 dark:border-neutral-low-400 dark:bg-neutral-low-500 [@media(min-width:480px)]:hidden"
        height={280}
        priority={priority}
        sizes="(max-width: 768px) 280px, 1px"
        src={mobileImage}
        srcSet={`
          ${APP.LOCAL_IMAGE_PATH}/general/placeholder.jpg 1w,
          ${mobileImage} 280w,
        `}
        width={280}
      />
      <Image
        alt={description}
        className="hidden w-full rounded-3 border border-neutral-high-200 bg-neutral-high-100 dark:border-neutral-low-400 dark:bg-neutral-low-500 xl:!hidden [@media(min-width:480px)]:block"
        height={280}
        priority={priority}
        sizes="(max-width: 1280px) 576px, 1px"
        src={tabletImage}
        srcSet={`
          ${APP.LOCAL_IMAGE_PATH}/general/placeholder.jpg 1w,
          ${tabletImage} 576w,
        `}
        width={576}
      />
      <Image
        alt={description}
        className="hidden w-full rounded-3 border border-neutral-high-200 bg-neutral-high-100 dark:border-neutral-low-400 dark:bg-neutral-low-500 xl:block"
        height={280}
        priority={priority}
        sizes="(min-width: 1280px) 1152px, 1px"
        src={desktopImage}
        srcSet={`
          ${APP.LOCAL_IMAGE_PATH}/general/placeholder.jpg 1w,
          ${desktopImage} 1152w,
        `}
        width={1152}
      />
    </>
  );

  return (
    <a
      ref={itemRef}
      className="block h-full w-full cursor-pointer"
      href={url}
      target="_blank"
      onClick={() => {
        sendEvent({
          category: gaEventCategory,
          action: `small_ad_click__${id}`,
          label: visibility,
        });

        trackEvent(
          'click home banner',
          {
            source_type: 'interaction',
            source_value: description,
            event_category: 'home',
            event_label: description,
            position,
          },
          { destination: 'ga4' }
        );
      }}
    >
      {sharedChildren}
    </a>
  );
};

AdCarouselItem.propTypes = {
  description: PropTypes.string.isRequired,
  desktopImage: PropTypes.string.isRequired,
  gaEventCategory: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  mobileImage: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  priority: PropTypes.bool.isRequired,
  tabletImage: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  visibility: PropTypes.string.isRequired,
};

export default AdCarouselItem;
